import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const OurBrand = () => {
  // Brand data
  const brands = [
    {
      name: 'Property Plateau',
      link: 'https://www.propertyplateau.com/',
      color: 'rgba(255, 255, 255, 0.85)',
        icon: '/Property_Plateau_logo.jpg',
      description: 'Premium real estate solutions'
    },
    {
      name: 'Holiday Homes',
      link: 'https://holidayhomes.propertyplateau.com/',
      color: 'rgba(255, 255, 255, 0.85)',
      icon: '/Holiday-Homes-Logo-2.png',
      description: 'Luxury vacation rentals'
    },
    {
      name: 'Property Plateau Times',
      link: 'https://propertyplateautimes.com/',
      color: 'rgba(255, 255, 255, 0.85)',
      icon: '/property-plateau-times.png',
      description: 'Real estate news portal'
    },
    {
      name: 'Latitude and Longitude',
      link: 'https://www.latitudeandlongitudegroup.com/',
      color: 'rgba(255, 255, 255, 0.85)',
      icon: '/Latitude_longitude_Black.png',
      description: 'Industry insights and interviews'
    }
  ];

  // FAQ data
  const faqs = [
    {
      question: 'What is Plateau?',
      answer: 'Plateau is a real estate brand offering premium housing solutions across prime locations.'
    },
    {
      question: 'How can I book a Holiday Home?',
      answer: 'Simply visit the Holiday Homes website, explore available properties, and book online or call us directly.'
    },
    {
      question: 'What is Property Plateau Times?',
      answer: 'It is our media platform for real estate news, insights, and property market trends.'
    },
    {
      question: 'Where can I listen to Real Estate Podcast?',
      answer: 'You can listen to our podcasts on all major platforms like Spotify, Apple Podcasts, and our website.'
    }
  ];

  // Animation states
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [currentFAQ, setCurrentFAQ] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const searchInputRef = useRef(null);

  // Animation timing configuration
  const animationConfig = {
    typingSpeed: 80,
    showingDuration: 3000,
    deletingSpeed: 40,
    betweenQuestions: 800
  };

  // Auto-typing effect
  useEffect(() => {
    let timeout;
    const currentQuestion = faqs[currentFAQ].question;

    if (isTyping) {
      if (displayText.length < currentQuestion.length) {
        timeout = setTimeout(() => {
          setDisplayText(currentQuestion.substring(0, displayText.length + 1));
        }, animationConfig.typingSpeed);
      } else {
        timeout = setTimeout(() => {
          setShowAnswer(true);
          timeout = setTimeout(() => {
            setShowAnswer(false);
            setIsTyping(false);
          }, animationConfig.showingDuration);
        }, animationConfig.betweenQuestions);
      }
    } else {
      if (displayText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayText(displayText.substring(0, displayText.length - 1));
        }, animationConfig.deletingSpeed);
      } else {
        timeout = setTimeout(() => {
          setCurrentFAQ((prev) => (prev + 1) % faqs.length);
          setIsTyping(true);
        }, animationConfig.betweenQuestions);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayText, isTyping, currentFAQ, faqs]);

  // Focus the search input
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Styles
  const styles = {
    page: {
      backgroundImage: `linear-gradient(rgba(255,255,255,0.2), rgba(255,255,255,0.2)), url('https://images.unsplash.com/photo-1580587771525-78b9dba3b914?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      minHeight: '100vh',
      padding: '60px 20px',
      fontFamily: "'Poppins', sans-serif",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    heading: {
      fontSize: '48px',
      color: '#2c3e50',
      marginBottom: '40px',
      textAlign: 'center',
      fontWeight: '700',
    },
    brandRowContainer: {
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'auto',
      padding: '20px 0',
      marginBottom: '60px',
      marginLeft: "40px",
      WebkitOverflowScrolling: 'touch',
    },
    brandRow: {
      display: 'flex',
      gap: '30px',
      padding: '0 30px',
       marginLeft: "70px",
      width: 'max-content',
    },
    brandCard: (color) => ({
      background: color,
      padding: '30px',
      borderRadius: '15px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
      textAlign: 'center',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px solid rgba(0,0,0,0.1)',
      backdropFilter: 'blur(5px)',
      transition: 'all 0.3s ease',
      minWidth: '280px',
      height: '350px',
      flexShrink: 0,
    }),
    brandTitle: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#2c3e50',
      marginTop: '20px',
    },
    brandDescription: {
      fontSize: '16px',
      color: '#7f8c8d',
      marginTop: '10px',
    },
    brandIcon: {
      width: '280px', // Increased from 100px to 150px
      height: '280px', // Increased from 100px to 150px
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px',
    },
    faqSection: {
      width: '100%',
      maxWidth: '1000px',
      background: 'rgba(255,255,255,0.95)',
      borderRadius: '20px',
      padding: '40px',
      boxShadow: '0px 12px 24px rgba(0,0,0,0.2)',
    },
    faqTitle: {
      fontSize: '36px',
      marginBottom: '30px',
      color: '#2c3e50',
      textAlign: 'center',
    },
    searchContainer: {
      position: 'relative',
      marginBottom: '40px',
    },
    searchInput: {
      width: '100%',
      padding: '15px 20px',
      fontSize: '18px',
      borderRadius: '50px',
      border: '2px solid #ddd',
      outline: 'none',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      transition: 'all 0.3s ease',
    },
    searchInputFocus: {
      borderColor: '#3498db',
      boxShadow: '0 4px 12px rgba(52,152,219,0.2)',
    },
    typingCursor: {
      display: 'inline-block',
      width: '8px',
      height: '20px',
      background: '#3498db',
      marginLeft: '4px',
      verticalAlign: 'middle',
      animation: 'blink 1s infinite',
    },
    answerBox: {
      background: '#f8f9fa',
      borderRadius: '10px',
      padding: '20px',
      marginTop: '20px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    },
    answerText: {
      fontSize: '16px',
      color: '#495057',
      lineHeight: '1.6',
    },
    progressIndicator: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '20px',
      gap: '10px',
    },
    progressDot: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      background: '#ddd',
      cursor: 'pointer',
    },
    activeDot: {
      background: '#3498db',
    }
  };

  return (
    <div style={styles.page}>
      <motion.h1 
        style={styles.heading}
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, type: 'spring' }}
      >
        Our Brands
      </motion.h1>

      <div style={styles.brandRowContainer}>
        <motion.div 
          style={styles.brandRow}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ staggerChildren: 0.1 }}
        >
          {brands.map((brand, index) => (
            <motion.div
              key={index}
              style={styles.brandCard(brand.color)}
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              whileHover={{ 
                y: -10,
                boxShadow: '0 15px 35px rgba(0,0,0,0.2)'
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => window.open(brand.link, '_blank')}
            >
              <div style={styles.brandIcon}>
                {/\.(png|jpe?g|svg|jpg)$/i.test(brand.icon) ? (
                  <img
                    src={brand.icon}
                    alt={brand.name}
                    style={{ 
                      height: '100%', 
                      maxWidth: '100%', 
                      objectFit: 'contain',
                      filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.2))'
                    }}
                  />
                ) : (
                  brand.icon
                )}
              </div>

              <div style={styles.brandTitle}>{brand.name}</div>
              <div style={styles.brandDescription}>{brand.description}</div>
              
              {hoveredIndex === index && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  style={{
                    position: 'absolute',
                    bottom: '20px',
                    fontSize: '14px',
                    color: '#2c3e50',
                    background: 'rgba(255,255,255,0.9)',
                    padding: '5px 15px',
                    borderRadius: '20px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                  }}
                >
                  Click to visit
                </motion.div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>

      <motion.div 
        style={styles.faqSection}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        <h2 style={styles.faqTitle}>Frequently Asked Questions</h2>
        
        <div style={styles.searchContainer}>
          <input
            ref={searchInputRef}
            type="text"
            value={displayText}
            readOnly
            style={{
              ...styles.searchInput,
              ...(isTyping ? styles.searchInputFocus : {})
            }}
            placeholder="Your question..."
          />
          {isTyping && <span style={styles.typingCursor}></span>}
        </div>

        <AnimatePresence>
          {showAnswer && (
            <motion.div
              style={styles.answerBox}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5 }}
            >
              <p style={styles.answerText}>{faqs[currentFAQ].answer}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div style={styles.progressIndicator}>
          {faqs.map((_, index) => (
            <div
              key={index}
              style={{
                ...styles.progressDot,
                ...(currentFAQ === index ? styles.activeDot : {})
              }}
              onClick={() => {
                setCurrentFAQ(index);
                setDisplayText('');
                setIsTyping(true);
                setShowAnswer(false);
              }}
            />
          ))}
        </div>
      </motion.div>

      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          /* Hide scrollbar but keep functionality */
          .brand-row-container::-webkit-scrollbar {
            display: none;
          }
          .brand-row-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
    </div>
  );
};

export default OurBrand;